// A Vectorworks-style database: criteria, the columns to materialise, and an
// optional "summarise items with the same" field.
//
// It owns ONE real row. That row's cells are ordinary `GI.*` formulas — the
// summary over every match — so the formula bar shows them and any other cell
// can reference them. The objects hang beneath it as SUB-ROWS, numbered 11.1,
// 11.2, … and drawn by the grid from the live data:
//
//   10  Unit Type      Area      Count   Area        ← ordinary cells (titles)
//   11  252 ▾          14,518 ▾  252 ▾   14,518 ▾    ← the database row
//   11.1 1 Bed 1P (M)  43.1      1       43.1        ← one per object
//   11.2 1 Bed 1P (M)  43.1      1       43.1
//   12  …                                            ← still row 12
//
// Sub-rows are outside the sheet's numbering on purpose. A database that grows
// by forty objects after a bake must not push row 12 to row 52 — every
// reference to a cell below it would silently point somewhere else. Nothing
// here inserts or deletes a row, ever.
//
// Grouped ("summarise items with the same"), there is one sub-row per group
// instead of one per object, as in Vectorworks.

import type { CellValue, Database, DbColumn, FormatId, Sheet } from '@/model/types';
import type { DataRow, GiraffeData } from '@/data/types';
import { applyFilter } from '@/data/filter';
import { aggregate, distinct } from '@/data/aggregate';
import { columnHeader, unitsOf } from '@/data/units';
import { prettyField } from '@/data/classify';
import { formatForField } from '@/engine/format';
import { setCellFormat, setCellRaw } from '@/model/workbook';
import type { Engine } from '@/engine/engine';
import { GI_FOR_AGG, call, filterArg, resolveAgg, sourceArg } from '@/giraffe/stamp';

/**
 * The matches, exactly as the database row's formulas will see them. `extra`
 * is the filter of the sheet the database is on, which its formulas apply too.
 */
export function matchesOf(
  db: Pick<Database, 'source' | 'criteria'>,
  data: GiraffeData,
  extra = ''
): { rows: DataRow[]; errors: string[] } {
  return applyFilter(data.rows(db.source), [...db.criteria, extra].filter((f) => f.trim() !== '').join(', '));
}


function isText(db: Database, column: DbColumn, data: GiraffeData): boolean {
  const cls = data.classOf(db.source, column.field);
  return cls === 'text' || cls === 'nested';
}


/**
 * The database row's formula for a column. A text column cannot be added up,
 * so under `auto` it shows the value when every object shares it and otherwise
 * how many objects there are — as "14 objects", not a bare 14: a number sitting
 * at the top of a column of usage names reads as a mistake.
 */
function summaryFormula(db: Database, column: DbColumn, data: GiraffeData): string {
  const cls = data.classOf(db.source, column.field);
  // The sheet's own filter is not written here: every GI call on the sheet
  // applies it already (model/sheetFilter.ts).
  const filter = filterArg(db.criteria, []);
  const src = sourceArg(db.source);
  const count = call('GI.COUNT', [filter, src]);
  if (column.agg === 'count') return count;
  if (isText(db, column, data) && column.agg === 'auto') {
    const field = JSON.stringify(column.field);
    const unique = call('GI.COUNTUNIQUE', [field, filter, src]);
    return `IF(${unique}=1,${call('GI.VALUE', [field, filter, src])},${count}&IF(${count}=1," object"," objects"))`;
  }
  const agg = resolveAgg(column.agg, cls);
  if (agg === 'count') return count;
  return call(GI_FOR_AGG[agg] ?? 'GI.TOTAL', [JSON.stringify(column.field), filter, src]);
}

/** "1 object", "14 objects" — what a text column says when its objects disagree. */
export function objectsLabel(n: number): string {
  return `${n} ${n === 1 ? 'object' : 'objects'}`;
}

/**
 * The fields a database summarises by, outermost first: none, `groupBy`, or
 * `groupBy` then `thenBy`.
 */
export function groupFields(db: Pick<Database, 'groupBy' | 'thenBy'>): string[] {
  if (!db.groupBy) return [];
  return db.thenBy && db.thenBy !== db.groupBy ? [db.groupBy, db.thenBy] : [db.groupBy];
}

/** The same reduction as `summaryFormula`, over a group, in JS — for a sub-row. */
function summaryValue(db: Database, column: DbColumn, rows: readonly DataRow[], data: GiraffeData, blank: readonly string[]): CellValue {
  // The group of objects with no value in a grouped field still needs a
  // name: without one it showed a count where the usage should be.
  if (blank.includes(column.field)) return `(no ${prettyField(column.field).toLowerCase()})`;
  if (column.agg === 'count') return rows.length;
  if (isText(db, column, data) && column.agg === 'auto') {
    const values = distinct(rows, column.field);
    return values.length === 1 ? values[0]! : objectsLabel(rows.length);
  }
  return aggregate(rows, column.field, column.agg, data.classOf(db.source, column.field)).value;
}

/** Title text: the user's label, or the field with its unit. */
export function columnTitle(column: DbColumn, data: GiraffeData): string {
  return column.label ?? columnHeader(column.field, unitsOf(data));
}

/** How a column's numbers are shown, in the database row and its sub-rows alike. */
export function columnFormat(db: Database, column: DbColumn, data: GiraffeData): FormatId | undefined {
  if (column.fmt) return column.fmt;
  if (column.agg === 'count') return 'number0';
  return isText(db, column, data) ? undefined : formatForField(column.field);
}

export interface SubRow {
  /** One value per column, in column order. */
  values: CellValue[];
}

/**
 * The objects behind each sub-row, in order: one object each, or — grouped — a
 * group's members. Grouped by two fields, a group is one combination of them,
 * and the combinations sharing the first field sit together. `blank` lists the
 * grouped fields this group has no value in.
 */
export function subRowMembers(db: Database, data: GiraffeData, extra = ''): { members: DataRow[]; blank: string[] }[] {
  const { rows } = matchesOf(db, data, extra);
  const fields = groupFields(db);
  if (fields.length === 0) return rows.map((row) => ({ members: [row], blank: [] }));
  const keyOf = (row: DataRow, field: string): string => {
    const v = row.properties[field];
    return v === null || v === undefined || v === '' ? '' : String(v).toLowerCase();
  };
  // Nested by field, so a second field never interleaves the first's groups.
  const outer = new Map<string, Map<string, DataRow[]>>();
  for (const row of rows) {
    const k1 = keyOf(row, fields[0]!);
    const k2 = fields[1] ? keyOf(row, fields[1]) : '';
    let inner = outer.get(k1);
    if (!inner) outer.set(k1, (inner = new Map()));
    const list = inner.get(k2);
    if (list) list.push(row);
    else inner.set(k2, [row]);
  }
  const out: { members: DataRow[]; blank: string[] }[] = [];
  for (const [k1, inner] of outer) {
    for (const [k2, members] of inner) {
      const blank = [k1 === '' ? fields[0]! : null, fields[1] && k2 === '' ? fields[1] : null].filter((f): f is string => f !== null);
      out.push({ members, blank });
    }
  }
  return out;
}

/** What hangs under the database row: one per object, or one per group. */
export function subRowsOf(db: Database, data: GiraffeData, extra = ''): SubRow[] {
  return subRowMembers(db, data, extra).map(({ members, blank }) => {
    if (groupFields(db).length === 0) {
      const row = members[0]!;
      return {
        values: db.columns.map((col) => {
          // Each object counts once, so a Count column adds up to the row above.
          if (col.agg === 'count') return 1;
          const v = row.properties[col.field];
          if (v === null || v === undefined) return null;
          return typeof v === 'number' || typeof v === 'string' || typeof v === 'boolean' ? v : String(v);
        }),
      };
    }
    return { values: db.columns.map((col) => summaryValue(db, col, members, data, blank)) };
  });
}

/**
 * Write (or rewrite) the database row. The caller owns undo.
 *
 * A NEW database also writes its column titles on the row above, as plain
 * cells — but only into cells that are empty, and never on row 1's nonexistent
 * row 0. They are the user's afterwards, like any other text.
 */
export function writeDatabase(
  sheet: Sheet,
  engine: Engine,
  db: Database,
  data: GiraffeData,
  isNew = false
): Database {
  const { row, col: c0 } = db.anchor;
  const width = Math.max(1, db.columns.length);
  const clearW = Math.max(db.width, width);

  for (let c = c0; c < c0 + clearW; c++) delete sheet.cells[`${row},${c}`];

  db.columns.forEach((col, j) => {
    const c = c0 + j;
    setCellRaw(sheet, row, c, `=${summaryFormula(db, col, data)}`);
    setCellFormat(sheet, row, c, columnFormat(db, col, data));
    const key = `${row},${c}`;
    sheet.cells[key] = { ...sheet.cells[key], s: { ...sheet.cells[key]?.s, bold: true } };
  });

  if (isNew && row > 1) {
    const above = row - 1;
    const free = db.columns.every((_, j) => sheet.cells[`${above},${c0 + j}`] === undefined);
    if (free) {
      db.columns.forEach((col, j) => {
        setCellRaw(sheet, above, c0 + j, columnTitle(col, data));
        const key = `${above},${c0 + j}`;
        sheet.cells[key] = { ...sheet.cells[key], s: { ...sheet.cells[key]?.s, bold: true } };
      });
    }
  }

  const next: Database = { ...db, width };
  sheet.databases = { ...sheet.databases, [db.id]: next };
  engine.onRangeEdited(sheet.id, Math.max(1, row - 1), c0, row, c0 + clearW - 1);
  return next;
}

/** Clear the database row and forget it. The titles above are ordinary cells and stay. */
export function removeDatabase(sheet: Sheet, engine: Engine, db: Database): void {
  const { row, col } = db.anchor;
  for (let c = col; c < col + db.width; c++) delete sheet.cells[`${row},${c}`];
  if (sheet.databases) {
    const { [db.id]: _gone, ...rest } = sheet.databases;
    sheet.databases = Object.keys(rest).length > 0 ? rest : undefined;
  }
  engine.onRangeEdited(sheet.id, row, col, row, col + db.width - 1);
}

/** The database whose row holds this cell, and which of its columns it is. */
export function databaseAt(sheet: Sheet, row: number, col: number): { db: Database; index: number } | null {
  for (const db of Object.values(sheet.databases ?? {})) {
    if (db.anchor.row !== row) continue;
    const index = col - db.anchor.col;
    if (index >= 0 && index < db.columns.length) return { db, index };
  }
  return null;
}

/** The database that owns this row, if one does. */
export function databaseOnRow(sheet: Sheet, row: number): Database | null {
  for (const db of Object.values(sheet.databases ?? {})) if (db.anchor.row === row) return db;
  return null;
}

/** Sensible first columns for a source: the name-ish text fields, then an area. */
export function defaultColumns(fields: readonly string[]): DbColumn[] {
  const pick = ['usage', 'name', 'grossArea', 'area'].filter((f) => fields.includes(f));
  const out: DbColumn[] = pick.slice(0, 3).map((field) => ({ field, agg: 'auto' }));
  return out.length > 0 ? out : fields.slice(0, 2).map((field) => ({ field, agg: 'auto' }));
}
